import React, {
  useRef,
  useState,
  useCallback,
  Fragment,
  useContext,
  useEffect,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import Button from "../ui-components/Button";
import checkIfBlockIsVisible from "../utils/checkIfBlockIsVisible";
import ConfiguratorSurveyBlockHeader from "./ConfiguratorSurveyBlockHeader";
import ConfiguratorSurveyBlockRender from "./ConfiguratorSurveyBlockRender";
import ConfiguratorSurveyRecursiveQuestionTable from "./ConfiguratorSurveyRecursiveQuestionTable";

import updateResponsesObject from "../utils/updateResponsesObject";
import getSelectQuestionsWithOneOption from "../utils/getSelectQuestionsWithOneOption";
import { isValidQuestion } from "../utils/blockValidation";
import getRecursiveTableColumns from "../utils/getRecursiveTableColumns";
import ConfiguratorContext from "./ConfiguratorContext";
import AuthenticationService from "../services/AuthenticationService";
import ConfiguratorSurveyRecursiveQuestionTableMode from "./ConfiguratorSurveyRecursiveQuestionTableMode";
import {removeAutogeneratedRecursiveResponses} from "../utils/removeAutogeneratedRecursiveResponses";

const ConfiguratorSurveyRecursiveQuestion = ({
  id,
  title,
  description,
  image_filename,
  blocks,
  list,
  onChange,
  realtime,
  summary,
  hidden_question,
  role,
  table_mode,
}) => {
  const context = useContext(ConfiguratorContext);
  const { configuration, responses } = context;
  const [currentRecursionResponses, setCurrentRecursionResponses] = useState(
    {}
  );
  const intl = useIntl();
  const columns = useRef(getRecursiveTableColumns(blocks, intl));
  const user = AuthenticationService.getUserData();
  
  // useEffect(() => {
  //   // update the current recursion responses based on the current element values
  //   // for the first time, so we are ready to add the first response row to the response table
  //   const newCurrentRecursionResponses = {};
  //   blocks.forEach((block) => {
  //     if (block.type === "question") {
  //       const value = responses[block.id];
  //       if (value) {
  //         newCurrentRecursionResponses[block.id] = {
  //           ...value,
  //           question: block,
  //         };
  //       }
  //     }
  //   });
  //   setCurrentRecursionResponses(newCurrentRecursionResponses);
  // }, [blocks, responses]);
  useEffect(() => {

    // update the current recursion responses based on the current element values
    // for the first time, so we are ready to add the first response row to the response table
    const newCurrentRecursionResponses = {};
    blocks.forEach((block) => {
      if (block.type === "question") {
        // FIX: quando entro qui responses non contiene l'id della domanda ricorsiva, come risolvo?
        // ---------------
        const value = responses[block.id];
        if (value) {
          newCurrentRecursionResponses[block.id] = {
            ...value,
            question: block,
          };
        }
      }
    });
    setCurrentRecursionResponses(newCurrentRecursionResponses);
  }, [blocks, responses]);

  const onChangeRecursive = useCallback(
    (value, option, block, shoudSetState = true) => {

      let responseObject = block? updateResponsesObject(
        currentRecursionResponses,
        block,
        value,
        option,
        undefined,
        context,
        true
      ): {};

      let questionWithOneOption = getSelectQuestionsWithOneOption(
        blocks.filter((b) => isValidQuestion(b)),
        value,
        block,
        false,
        {
          ...context,
          responses: {
            // exclude current recursive question responses, since previous iterations might interfere with the current one
            ..._.omit(responses, [id]),
            ...responseObject
          }
        },
        user
      );

      // If we have a question with one option, maybe its result triggers another question with one option
      let alreadyChecked = [];
      while (
        Object.keys(questionWithOneOption).length > alreadyChecked.length
      ) {
        // eslint-disable-next-line no-loop-func
        const keyStr = Object.keys(questionWithOneOption).filter(
          (x) => !alreadyChecked.includes(x)
        )[0];
        const key = Number(keyStr);
        responseObject = updateResponsesObject(
          responseObject,
          blocks.find((x) => x.id === key),
          questionWithOneOption[key]["value"],
          questionWithOneOption[key]["option"],
          undefined,
          context,
          true
        );
        const newQuestionsWithOneOption = getSelectQuestionsWithOneOption(
          blocks.filter((b) => isValidQuestion(b)),
          questionWithOneOption[key]["value"],
          blocks.find((x) => x.id === key),
          false,
          {
            ...context,
            responses: {
              // exclude current recursive question responses, since previous iterations might interfere with the current one
              ..._.omit(responses, [id]),
              ...responseObject
            }
          },
          user
        );
        alreadyChecked = [...alreadyChecked, keyStr];
        questionWithOneOption = {
          ...questionWithOneOption,
          ...newQuestionsWithOneOption,
        };
      }

      const modified = { ...responseObject, ...questionWithOneOption };
      if (shoudSetState) {
        setCurrentRecursionResponses(modified);
      }
      return modified;
    },
    [
      currentRecursionResponses,
      setCurrentRecursionResponses,
      responses,
      blocks,
      user,
      context,
    ]
  );

  useEffect(() => {
    // update the current recursion responses based on the current element values
    // for the first time, so we are ready to add the first response row to the response table
    const newCurrentRecursionResponses = {};
    blocks.forEach((block) => {
      if (block.type === "question") {
        const value = responses[block.id];
        if (value) {
          newCurrentRecursionResponses[block.id] = {
            ...value,
            question: block,
          };
        }
      }
    });
    const modified = onChangeRecursive(
      undefined,
      undefined,
      undefined,
      true
    )
    setCurrentRecursionResponses({
      ...newCurrentRecursionResponses,
      ...modified,
    });
  }, [blocks, responses]);

  if (!blocks?.length) {
    return null;
  }

  const hidden = hidden_question.includes(`role_type_${role}`) ? "hidden" : "";
  const responsesToCheck = {
    ..._.omit(responses, [id]),
    ...currentRecursionResponses,
  };
  const currentRecursionBlocksOutputs = blocks.filter(
    (block) =>
      block.type !== "output" &&
      checkIfBlockIsVisible(block, {
        ...context,
        responses: responsesToCheck,
        configuration,
      })
  );
  const currentRecursionBlockQuestions = blocks.filter(
    (block) =>
      block.type === "question" &&
      checkIfBlockIsVisible(block, {
        ...context,
        responses: responsesToCheck,
        configuration,
      })
  );


  return (
    <>
      <ConfiguratorSurveyBlockHeader
        title={title}
        description={description}
        image_filename={image_filename}
        realtime={realtime}
        hidden={hidden}
      />
      {table_mode && !realtime ? (
        <ConfiguratorSurveyRecursiveQuestionTableMode
          blocks={blocks}
          responsesToCheck={responsesToCheck}
          onChange={onChange}
          list={list}
          onChangeRecursive={onChangeRecursive}
          role={role}
          user={user}
          currentRecursionResponses={currentRecursionResponses}
          setCurrentRecursionResponses={setCurrentRecursionResponses}
          editable={!summary}
          realtime
          summary
        />
      ) : (
        <div
          className={`flex flex-col w-full bg-gray-100 px-4 py-4 my-4 ${hidden}`}
        >
          <div className={onChange ? "mb-8" : ""} />
          {onChange ? (

            <Fragment>
              {currentRecursionBlocksOutputs.map((block) => (
                <ConfiguratorSurveyBlockRender
                  key={block.id}
                  recursiveResponses={responsesToCheck}
                  onChange={(value, option) =>
                    onChangeRecursive(value, option, block)
                  }
                  role={role}
                  block={block}
                />
              ))}
              <div className="w-full flex justify-end mt-8">
                <Button
                  styleType="secondary"
                  onClick={() => {
                    // hubspot ticket 42912186232
                    // we need to clear autogenerated responses of
                    // questions inside a recursive question
                    removeAutogeneratedRecursiveResponses(context, id);
                    setCurrentRecursionResponses({});
                    onChangeRecursive(
                      undefined,
                      undefined,
                      undefined,
                      true
                    )
                  }}
                  className="mr-4"
                >
                  <FormattedMessage id="clear" defaultMessage="CLEAR"/>
                </Button>
                <Button
                  onClick={() => {
                    const missingQuestions =
                      currentRecursionBlockQuestions.filter(
                        (block) => {

                          const isRequired = block.required;
                          const isNotInCurrentRecursionResponses = !currentRecursionResponses[block.id];
                          const isKColorInput = block.input_type === "k_color_input";
                          const isKColorProductMissing = !currentRecursionResponses[block.id]?.value?.kColorProduct;
                          const isColorMissing = !currentRecursionResponses[block.id]?.value?.color;
                          const isProductPackageMissing = !currentRecursionResponses[block.id]?.value?.productPackage;

                          // A question is missing if it is required and not in the current recursion responses
                          // or if it is a k_color_input and the kColorProduct or color is missing

                          return (
                            isRequired &&
                            (isNotInCurrentRecursionResponses ||
                              (isKColorInput &&
                                (isKColorProductMissing || isColorMissing || isProductPackageMissing)))
                          );
                        });
                    if (missingQuestions.length) {
                      console.log(
                        'missingQuestions', missingQuestions
                      )
                      return;
                    }

                    onChange([...list, currentRecursionResponses]);
                    setCurrentRecursionResponses({});
                    removeAutogeneratedRecursiveResponses(context, id);
                  }}
                >
                  <FormattedMessage id="add" defaultMessage="ADD"/>
                </Button>
              </div>
            </Fragment>
          ) : null}
          {list.length ? (
            <ConfiguratorSurveyRecursiveQuestionTable
              recursiveQuestionId={id}
              list={list}
              onChange={onChange}
              blocks={blocks}
              columns={columns.current}
              realtime={realtime}
              summary={summary}
              role={role}
            />
          ) : null}
        </div>
      )}
    </>
  );
};

export default ConfiguratorSurveyRecursiveQuestion;
